import {
  AfterViewInit,
  ChangeDetectorRef,
  Component, CUSTOM_ELEMENTS_SCHEMA,
  inject, input, model,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ButtonComponent,
  ButtonOptions,
  DateComponent,
  DateOptions,
  InputComponent,
  InputOptions,
  SelectComponent,
  SelectOptions,
} from '@shared/components';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormGroupDirective,
  NgForm,
  ReactiveFormsModule, ValidationErrors, ValidatorFn,
  Validators,
} from '@angular/forms';
import { TranslateService, Translation } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ButtonTypes, InputModes, InputTypes } from '@core/enums';
import {assets, FILTER_ARC_NUMBERS, FILTER_CIC_NUMBERS, FILTER_ONLY_NUMBERS} from '@core/constants';
import { APIService } from '@core/services';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import moment from 'moment';
import { PatientSearch } from '@core/classes/other/patient-search';
import { OTPStatuses } from '@core/enums/otpstatuses';
import { OTPResponse } from '@core/classes/other/otpresponse';
import { PatientDemographics } from '@core/classes/other/patient-demographics';
import { AlertComponent, AlertOptions } from '@shared/components/alert/alert.component';
import { AlertTypes } from '@core/enums/alert-types';
import {checkDate, decodeToken} from '@core/helpers';
import { PatientData } from '@core/classes/other/patient-data';
import { PatientSession } from '@core/classes/other/patient-session';
import { PatientService } from '@core/services/patient.service';
import {NgOptimizedImage} from '@angular/common';

@UntilDestroy()
@Component({
  selector: 'ncp-cy',
  imports: [
    ReactiveFormsModule,
    SelectComponent,
    InputComponent,
    DateComponent,
    ButtonComponent,
    AlertComponent
  ],
  templateUrl: './cy.component.html',
  styleUrl: './cy.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class CYComponent implements OnInit, AfterViewInit {
  @ViewChild('sfrm') protected searchFormElement?: NgForm;
  @ViewChild('sfrm') protected searchFormDirective?: FormGroupDirective;
  @ViewChild('dfrm') protected demographicsFormElement?: NgForm;
  @ViewChild('ofrm') protected otpFormElement?: NgForm;
  @ViewChild('resendLink') protected resendBtnElement?: HTMLAnchorElement;
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private cdref = inject(ChangeDetectorRef);
  private apiService = inject(APIService);
  private patientService = inject(PatientService);
  private readonly translate = inject(TranslateService);
  protected translations!: Translation;
  protected interval: number = 0;
  countdown = model<number>(300); // start at 300 seconds
  countdownText = model<string>('');
  resendDisabled = model<boolean>(false);

  protected searchForm: FormGroup = new FormGroup({
    patientId: new FormControl<string>('', [
      Validators.required,
      Validators.pattern(FILTER_CIC_NUMBERS),
    ]),
    dob: new FormControl<string>('', [Validators.required]),
  }, {
    validators: [this.formValidator('sfrm')]
  });

  protected demographicsForm = new FormGroup({
    address: new FormControl('', [Validators.required]),
    addressNumber: new FormControl('', [Validators.required]),
    postalCode: new FormControl('', [Validators.required]),
    city: new FormControl('', [Validators.required]),
    country: new FormControl('Cyprus', []),
    email: new FormControl('', [Validators.email]),
    telephone: new FormControl('', [Validators.required]),
  }, {
    validators: [this.formValidator('dfrm')]
  });

  protected otpForm: FormGroup = new FormGroup({
    otp: new FormControl<string>('', [
      Validators.required /*, Validators.pattern(FILTER_OTP_NUMBERS)*/,
    ]),
  }, {
    validators: [this.formValidator('ofrm')]
  });

  formValidator(type: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let errors: ValidationErrors | null = {};
      let today = moment();

      switch (type) {
        case 'sfrm': {
          let patientId = (control as FormGroup).controls['patientId'];
          let dob = (control as FormGroup).controls['dob'];

          if(patientId.value === undefined || patientId.value === null || patientId.value === '') {
            patientId.setErrors({sfrm_patientIdInvalid: true});
            errors['sfrm_patientIdInvalid'] = true;
          } else {
            let pattern = this.idCardInputOptions?.().pattern;
            let choice = this.searchSelectOptions?.().model;

            if(!pattern?.test(patientId.value)) {
              if(choice == 0) {
                console.log('choice 0');
                patientId.setErrors({sfrm_patientIdCICPatternInvalid: true});
                errors['sfrm_patientIdCICPatternInvalid'] = true;
              } else {
                console.log('choice 1');
                patientId.setErrors({sfrm_patientIdARCPatternInvalid: true});
                errors['sfrm_patientIdARCPatternInvalid'] = true;
              }
            }
          }

          if((!checkDate(dob.value).isValid())) {
            dob.setErrors({sfrm_dobDateInvalid: true});
            errors['sfrm_dobDateInvalid'] = true;
          } else {
            if(checkDate(dob.value).isSameOrAfter(today)) {
              dob.setErrors({sfrm_dobBeforeToday: true});
              errors['sfrm_dobBeforeToday'] = true;
            } else {
              dob.setErrors(null);
            }
          }
        } break;
        case 'dfrm': {
          let address = (control as FormGroup).controls['address'];
          let addressNumber = (control as FormGroup).controls['addressNumber'];
          let postalCode = (control as FormGroup).controls['postalCode'];
          let city = (control as FormGroup).controls['city'];
          let email = (control as FormGroup).controls['email'];
          let telephone = (control as FormGroup).controls['telephone'];

          if(address.value === undefined || address.value === null || address.value === '') {
            address.setErrors({dfrm_addressInvalid: true});
            errors['dfrm_addressInvalid'] = true;
          }

          if(addressNumber.value === undefined || addressNumber.value === null || addressNumber.value === '') {
            addressNumber.setErrors({dfrm_addressNumberInvalid: true});
            errors['dfrm_addressNumberInvalid'] = true;
          }

          if(postalCode.value === undefined || postalCode.value === null || postalCode.value === '') {
            postalCode.setErrors({dfrm_postalCodeInvalid: true});
            errors['dfrm_postalCodeInvalid'] = true;
          }

          if(city.value === undefined || city.value === null || city.value === '') {
            city.setErrors({dfrm_cityInvalid: true});
            errors['dfrm_cityInvalid'] = true;
          }

          if(email.value && Object.keys(email.errors || {}).length > 0) {
            email.setErrors({dfrm_emailPatternInvalid: true});
            errors['dfrm_emailPatternInvalid'] = true;
          }

          if(telephone.value === undefined || telephone.value === null || telephone.value === '') {
            telephone.setErrors({dfrm_telephoneInvalid: true});
            errors['dfrm_telephoneInvalid'] = true;
          }

        } break;
        case 'ofrm': {
          let otp = (control as FormGroup).controls['otp'];

          if(otp.value === undefined || otp.value === null || otp.value === '') {
            otp.setErrors({ofrm_otpInvalid: true});
            errors['ofrm_otpInvalid'] = true;
          }
        } break;
        default: {

        } break;
      }

      if(Object.keys(errors).length === 0) {
        this.errorsAlertOptions?.set({
          ...this.errorsAlertOptions?.(),
          show: false,
        });
      }
      return Object.keys(errors).length > 0 ? errors : null;
    }
  }

  validate = (form: FormGroup): boolean => {
    Object.keys(form.controls).forEach((control) => {
      console.log(control);
      console.log(
      form.controls[control].errors
      );
      form.controls[control].markAsTouched();
    })

    return true;
  }

  /*
  <div class="col-3">
    <ncp-input [(options)]="addressInputOptions" />
  </div>
  <div class="col-3">
    <ncp-input [(options)]="countryInputOptions" />
  </div>
  <div class="col-3">
    <ncp-input [(options)]="emailInputOptions" />
  </div>
  <div class="col-3">
    <ncp-input [(options)]="mobilePhoneInputOptions" />
   */

  // Search Form
  protected searchSelectOptions = signal<SelectOptions>({});
  protected idCardInputOptions = signal<InputOptions>({});
  protected dobDateOptions = signal<DateOptions>({});

  // Demographics Form
  protected addressInputOptions = signal<InputOptions>({});
  protected addressNumberInputOptions = signal<InputOptions>({});
  protected addressPostCodeInputOptions = signal<InputOptions>({});
  protected addressCityInputOptions = signal<InputOptions>({});
  protected addressCountryInputOptions = signal<InputOptions>({});
  protected countryInputOptions = signal<InputOptions>({});
  protected emailInputOptions = signal<InputOptions>({});
  protected telephoneInputOptions = signal<InputOptions>({});
  protected patientSearchData = signal<PatientSearch | null>(null);

  // OTP
  protected otpInputOptions = signal<InputOptions>({});

  // Submit Buttons
  protected demographicsSubmitButtonOptions = signal<ButtonOptions>({});
  protected otpSubmitButtonOptions = signal<ButtonOptions>({});
  protected searchSubmitButtonOptions = signal<ButtonOptions>({});

  // Alert
  protected errorsAlertOptions = signal<AlertOptions>({});

  private _idType: string | number = '';

  get idType(): string | number {
    return this._idType;
  }

  set idType(value: string | number) {
    this._idType = value;
  }

  ngOnInit(): void {
    this.idType = 0;
    this.translations = this.translate.instant('cy');
    this.translate.onLangChange.pipe(untilDestroyed(this)).subscribe((event) => {
      this.translations = this.translate.instant('cy');
    });

    this.searchSelectOptions.set(
      new SelectOptions({
        label: {
          text: 'documentType',
        },
        values: [
          {
            id: 0,
            value: 'cypriot',
          },
          {
            id: 1,
            value: 'arc',
          },
        ],
        model: this.idType,
        onModelChange: this.onIdTypeChange,
      })
    );

    this.idCardInputOptions.set(
      new InputOptions({
        id: 'idNumber',
        label: {
          text: 'idNumber',
        },
        type: InputTypes.text,
        mode: InputModes.numeric,
        name: 'patientId',
        pattern: this.idType === 0 ? FILTER_CIC_NUMBERS : FILTER_ARC_NUMBERS,
        formControl: this.patientId,
        initialValue: '',
        onInput: this.onNumericInput,
        required: true
      })
    );

    this.dobDateOptions.set(
      new DateOptions({
        id: 'dobDate',
        label: {
          text: 'dob',
        },
        name: 'dobDate',
        initialValue: '',
        formControl: this.dob,
        required: true,
        maxDate: moment().format('YYYY-MM-DD'),
        onInput: this.onDateInput,
      })
    );

    this.demographicsSubmitButtonOptions.set(
      new ButtonOptions({
        id: 'submitButton',
        class: 'btn btn-primary',
        text: 'Submit',
        type: ButtonTypes.submit,
        disabled: this.searchForm.valid,
        onClick: (event) => {
          this.demographicsSubmitButtonOptions().isLoading = true;
        }
      })
    );

    this.otpSubmitButtonOptions.set(
      new ButtonOptions({
        id: 'submitButton',
        class: 'btn btn-primary',
        text: 'Submit',
        type: ButtonTypes.submit,
        disabled: this.searchForm.valid,
        onClick: (event) => {
          this.otpSubmitButtonOptions().isLoading = true;
        }
      })
    );

    this.searchSubmitButtonOptions.set(
      new ButtonOptions({
        id: 'submitButton',
        class: 'btn btn-primary',
        text: 'Submit',
        type: ButtonTypes.submit,
        icon: 'bi bi-search',
        disabled: this.searchForm.valid,
        onClick: (event) => {
          this.searchSubmitButtonOptions().isLoading = true;
        }
      })
    );

    this.addressInputOptions.set(
      new InputOptions({
        id: 'address',
        label: {
          text: 'address',
        },
        type: InputTypes.text,
        name: 'address',
        formControl: this.address,
        initialValue: '',
        required: true
      })
    );

    this.addressNumberInputOptions.set(
      new InputOptions({
        id: 'number',
        label: {
          text: 'number',
        },
        type: InputTypes.text,
        name: 'address',
        pattern: /[a-zA-Z0-9]+$/,
        formControl: this.number,
        initialValue: '',
        required: true
      })
    );

    this.addressPostCodeInputOptions.set(
      new InputOptions({
        id: 'post_code',
        label: {
          text: 'post_code',
        },
        type: InputTypes.text,
        name: 'address',
        formControl: this.postCode,
        initialValue: '',
        required: true
      })
    );

    this.addressCityInputOptions.set(
      new InputOptions({
        id: 'city',
        label: {
          text: 'city',
        },
        type: InputTypes.text,
        name: 'address',
        formControl: this.city,
        initialValue: '',
        required: true
      })
    );

    this.addressCountryInputOptions.set(
      new InputOptions({
        id: 'country',
        label: {
          text: 'country',
        },
        type: InputTypes.text,
        name: 'country',
        formControl: this.country,
        initialValue: 'cy',
        disabled: true
      })
    );

    this.emailInputOptions.set(
      new InputOptions({
        id: 'email',
        label: {
          text: 'email',
        },
        type: InputTypes.email,
        name: 'email',
        formControl: this.email,
        initialValue: '',
      })
    );

    this.telephoneInputOptions.set(
      new InputOptions({
        id: 'telephone',
        label: {
          text: 'telephone',
        },
        type: InputTypes.tel,
        name: 'telephone',
        formControl: this.telephone,
        initialValue: '',
        required: true
      })
    );

    this.otpInputOptions.set(
      new InputOptions({
        id: 'otp',
        label: {
          text: 'otp',
        },
        type: InputTypes.text,
        mode: InputModes.numeric,
        name: 'otp',
        //pattern: FILTER_OTP_NUMBERS,
        formControl: this.otp,
        initialValue: '',
        onInput: this.onNumericInput,
      })
    );

    this.errorsAlertOptions.set({
      id: 'ErrorsAlert',
      type: AlertTypes.danger,
      title: 'error',
      text: 'patient_not_found',
      show: false,
    });
  }

  startCountdown() {
    console.log(this.interval);
    this.countdown.set(300);
    this.interval = setInterval(() => {
      this.countdown.set(this.countdown() - 1);
      this.showCountdownText();
      if (this.countdown() === 0) {
        clearInterval(this.interval);
      }
      //this.changeDetectorRef.detectChanges();
    }, 1000); // every 1 second
  }

  resendOTP() {
    this.resendDisabled.set(true);
    //this.countdown.set(300);
    this.apiService
      .call({
        path: '/patient/get',
        body: new HttpParams()
          .set('patientId', this.patientId.value.toString().padStart(10, '0'))
          .set('dob', moment(this.dob.value).format('YYYY-MM-DD').toString()),
      })
      ?.subscribe((res) => {
        if (res.status === 'OK' && res.success && res.data) {
          switch (this.patientSearchData()?.otpStatus) {
            case OTPStatuses.NotFound:
            {
              this.errorsAlertOptions.set({
                ...this.errorsAlertOptions(),
                show: true,
                text: 'patient_not_found',
              });
            }
              break;
            case OTPStatuses.Locked:
            {
              this.errorsAlertOptions.set({
                ...this.errorsAlertOptions(),
                show: true,
                text: 'patient_locked',
              });
            }
              break;
            case OTPStatuses.Error:
            {
              this.errorsAlertOptions.set({
                ...this.errorsAlertOptions(),
                show: true,
                text: 'patient_error',
              });
            }
              break;
          }
        }
        this.resendDisabled.set(false);
        this.countdownText.set('');
        clearInterval(this.interval);
        this.startCountdown();
      })
  }

  showCountdownText() {
  console.log(this.countdown());
    if (Math.floor(this.countdown() / 60) > 1) {
      let replacingText = '' + Math.floor(this.countdown() / 60) + ' ' + this.translations.minutes;
      if((this.countdown() % 60) > 1) {
        replacingText += ' ' + this.translations.and + ' ' + (this.countdown() % 60) + ' ' + this.translations.seconds;
      } else if((this.countdown() % 60) === 1) {
        replacingText += ' ' + this.translations.and + ' ' + (this.countdown() % 60) + ' ' + this.translations.second;
      }
      this.countdownText.set(this.translations.otp_expires.replace('{1}', replacingText));
    } else if (Math.floor(this.countdown() / 60) === 1) {
      let replacingText = '' + Math.floor(this.countdown() / 60) + ' ' + this.translations.minute;
      if((this.countdown() % 60) > 1) {
        replacingText += ' ' + this.translations.and + ' ' + (this.countdown() % 60) + ' ' + this.translations.seconds;
      } else if((this.countdown() % 60) === 1) {
        replacingText += ' ' + this.translations.and + ' ' +(this.countdown() % 60) + ' ' + this.translations.second;
      }
      this.countdownText.set(this.translations.otp_expires.replace('{1}', replacingText));
    } else {
      let replacingText = '';
      if((this.countdown() % 60) > 1) {
        replacingText += ' ' + (this.countdown() % 60) + ' ' + this.translations.seconds;
      } else if((this.countdown() % 60) === 1) {
        replacingText += ' ' +(this.countdown() % 60) + ' ' + this.translations.second;
      }
      this.countdownText.set(this.translations.otp_expires.replace('{1}', replacingText));
    }
  }

  ngAfterViewInit(): void {
    this.cdref.detectChanges();
  }

  get patientId() {
    return this.searchForm.get('patientId') as FormControl;
  }

  get dob() {
    return this.searchForm.get('dob') as FormControl;
  }

  get address() {
    return this.demographicsForm.get('address') as FormControl;
  }

  get number() {
    return this.demographicsForm.get('addressNumber') as FormControl;
  }

  get postCode() {
    return this.demographicsForm.get('postalCode') as FormControl;
  }

  get city() {
    return this.demographicsForm.get('city') as FormControl;
  }

  get country() {
    return this.demographicsForm.get('country') as FormControl;
  }

  get email() {
    return this.demographicsForm.get('email') as FormControl;
  }

  get telephone() {
    return this.demographicsForm.get('telephone') as FormControl;
  }

  get otp() {
    return this.otpForm.get('otp') as FormControl;
  }

  onFormSubmit = (form: FormGroupDirective) => {
    this.errorsAlertOptions.set({
      ...this.errorsAlertOptions(),
      show: false,
    });

    if (form.form === this.searchFormElement?.form) {
      if(this.validate(this.searchFormElement?.form) && form.invalid) {
        console.log('this.form.is.invalid');
        console.log(this.searchFormElement?.form.errors);
        this.searchSubmitButtonOptions().isLoading = false;
        this.errorsAlertOptions().show = true;
        return;
      }

      this.apiService
        .call({
          path: '/patient/get',
          body: new HttpParams()
            .set('patientId', this.patientId.value.toString().padStart(10, '0'))
            .set('dob', moment(this.dob.value).format('YYYY-MM-DD').toString()),
        })
        ?.subscribe((res) => {
          if (res.status === 'OK' && res.success && res.data) {
            //res.data.otpStatus = OTPStatuses.AddDemographics
            //res.data.otpStatus = OTPStatuses.Error
            this.patientSearchData.set(res.data as PatientSearch);

            switch (this.patientSearchData()?.otpStatus) {
              case OTPStatuses.NotFound:
                {
                  this.errorsAlertOptions.set({
                    ...this.errorsAlertOptions(),
                    show: true,
                    text: 'patient_not_found',
                  });
                }
                break;
              case OTPStatuses.Locked:
                {
                  this.errorsAlertOptions.set({
                    ...this.errorsAlertOptions(),
                    show: true,
                    text: 'patient_locked',
                  });
                }
                break;
              case OTPStatuses.Error:
                {
                  this.errorsAlertOptions.set({
                    ...this.errorsAlertOptions(),
                    show: true,
                    text: 'patient_error',
                  });
                }
                break;
            }
          }
          clearInterval(this.interval);
          this.startCountdown();
        });
    } else if (form.form === this.demographicsFormElement?.form) {
      if(this.validate(this.demographicsFormElement?.form) && form.invalid) {
        console.log('this.form.is.invalid');
        console.log(this.demographicsFormElement?.form.errors);
        this.demographicsSubmitButtonOptions().isLoading = false;
        this.errorsAlertOptions().show = true;
        return;
      }

      this.apiService
        .call({
          path: '/patient/demographics',
          body: new HttpParams()
            .set('address', this.address.value)
            .set('addressNumber', this.number.value)
            .set('postalCode', this.postCode.value)
            .set('city', this.city.value)
            .set('country', 'cy')
            .set('email', this.email.value)
            .set('telephone', this.telephone.value),
        })
        ?.subscribe((res) => {
          if (res.status === 'OK' && res.success && res.data) {
            let patientDemographics = res.data as PatientDemographics;

            this.patientSearchData.set({ otpStatus: OTPStatuses.OTPOk });
          } else {
            this.errorsAlertOptions.set({
              ...this.errorsAlertOptions(),
              show: true,
              text: 'demographics_error',
            });
          }

          this.demographicsSubmitButtonOptions().isLoading = false;
        });
    } else if (form.form === this.otpFormElement?.form) {
      if(this.validate(this.otpFormElement?.form) && form.invalid) {
        console.log('this.form.is.invalid');
        console.log(this.otpFormElement?.form.errors);
        this.otpSubmitButtonOptions().isLoading = false;
        this.errorsAlertOptions().show = true;
        return;
      }

      this.apiService
        .call({
          path: '/patient/set-OTP',
          body: new HttpParams().set('otp', this.otp.value),
        })
        ?.subscribe((res) => {
          if (res.status === 'OK' && res.success && res.data) {
            if (res.data.otpResponce === 1) {
              clearInterval(this.interval);
              const token: string = (res.data as OTPResponse).pToken || '';
              const data = (decodeToken(token) || {}) as PatientData;

              this.apiService
                .call({
                  path: '/latested-version',
                  headers: new HttpHeaders({}).set('pToken', token),
                })
                ?.subscribe((lvres) => {
                  if (lvres && lvres.status === 1 && lvres.data) {
                    let patient: PatientSession = {
                      id: data.patientId,
                      token: token,
                      version: {
                        id: -1,
                        new: -1,
                        details: null,
                        latest_dt: lvres.data.v_dt,
                        latest_author: lvres.data.author,
                      },
                      data: data,
                    };

                    this.patientService.patient = patient;

                    this.otpSubmitButtonOptions().isLoading = false;

                    this.router.navigate(['cy', 'patient']);
                  }
                });
            } else {
              this.otpSubmitButtonOptions().isLoading = false;

              this.errorsAlertOptions.set({
                ...this.errorsAlertOptions(),
                show: true,
                text: 'otp_error',
              });
            }
          } else {
            this.errorsAlertOptions.set({
              ...this.errorsAlertOptions(),
              show: true,
              text: 'otp_error',
            });
          }
        });
    }
  };

  protected onNumericInput = (input: HTMLInputElement): any => {
    this.errorsAlertOptions.set({
      ...this.errorsAlertOptions(),
      show: false,
    });
    return input.value.replace(FILTER_ONLY_NUMBERS, '');
  };

  protected onDateInput = (input: HTMLInputElement): any => {
    this.errorsAlertOptions.set({
      ...this.errorsAlertOptions(),
      show: false,
    });
    return input.value;
  };

  protected onIdTypeChange = (value?: number | string) => {
    this.errorsAlertOptions.set({
      ...this.errorsAlertOptions(),
      show: false,
    });

    switch (value) {
      case 0:
        {
          this.idCardInputOptions().pattern = FILTER_CIC_NUMBERS;
          this.patientId.setValidators([
            Validators.required,
            Validators.pattern(FILTER_CIC_NUMBERS),
          ]);
        }
        break;
      case 1:
        {
          this.idCardInputOptions().pattern = FILTER_ARC_NUMBERS;
          this.patientId.setValidators([
            Validators.required,
            Validators.pattern(FILTER_ARC_NUMBERS),
          ]);
        }
        break;
    }
    this.patientId.reset();
  };
  protected readonly OTPStatuses = OTPStatuses;
  protected readonly AlertTypes = AlertTypes;
  protected readonly Object = Object;
  protected readonly assets = assets;
}
